export const INVENTORY_STATUS_FILTERS = [
  { label: 'All Items', value: 0 },
  { label: 'Tracking ON', value: 1 },
  { label: 'Tracking OFF', value: 2 },
  { label: 'Low Inventory', value: 3 },
  { label: 'Out of Stock', value: 4 },
];

// Normalize a quantity/threshold cell to a number or null.
// Empty string / null / undefined -> null; otherwise Number(value).
const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

export const buildInventoryListQuery = ({
  storeid,
  companyid,
  categoryid,
  search,
  statusFilter,
} = {}) => {
  const params = new URLSearchParams();
  params.set('storeid', storeid ?? '');
  if (companyid) params.set('companyid', companyid);
  if (categoryid) params.set('categoryid', categoryid);
  if (search && search.trim() !== '') params.set('search', search.trim());
  params.set('statusFilter', String(statusFilter ?? 0));
  return '?' + params.toString();
};

// Client-side mirror of the backend status filter (and of statusBadgeFor's
// semantics), kept as a safety net so the filter works even against a server
// that does not honor the statusFilter query param. `draft` holds the live
// trackingEnabled toggle per itemId; the isOutOfStock/isLowInventory flags come
// from the server row and only matter while tracking is on. Categories left
// with no matching items are dropped so no empty headers render.
export const applyClientStatusFilter = (categories = [], statusFilter = 0, draft = {}) => {
  const value = Number(statusFilter) || 0;
  if (value === 0) return categories;

  const keep = (row) => {
    const tracking = !!draft[row.itemId]?.trackingEnabled;
    switch (value) {
      case 1:
        return tracking;
      case 2:
        return !tracking;
      case 3:
        return tracking && !!row.isLowInventory;
      case 4:
        return tracking && !!row.isOutOfStock;
      default:
        return true;
    }
  };

  return categories
    .map((cat) => ({ ...cat, items: (cat.items || []).filter(keep) }))
    .filter((cat) => cat.items.length > 0);
};

export const validateInventoryDraftRow = (row = {}) => {
  const errors = {};
  const qty = toNullableNumber(row.quantity);
  const threshold = toNullableNumber(row.lowStockThreshold);

  if (row.trackingEnabled && qty === null) {
    errors.quantity = 'Quantity is required when tracking is on.';
  } else if (qty !== null && qty < 0) {
    errors.quantity = 'Quantity cannot be negative.';
  }

  if (threshold !== null && threshold < 0) {
    errors.lowStockThreshold = 'Low stock threshold cannot be negative.';
  }

  return errors;
};

export const diffInventoryDraft = (categories = [], draft = {}) => {
  const updates = [];
  categories.forEach((cat) => {
    (cat.items || []).forEach((item) => {
      const d = draft[item.itemId];
      if (!d) return;
      const draftTracking = !!d.trackingEnabled;
      const draftQty = toNullableNumber(d.quantity);
      const draftThreshold = toNullableNumber(d.lowStockThreshold);
      const serverQty = toNullableNumber(item.quantity);
      const serverThreshold = toNullableNumber(item.lowStockThreshold);
      const changed =
        draftTracking !== !!item.trackingEnabled ||
        draftQty !== serverQty ||
        draftThreshold !== serverThreshold;
      if (changed) {
        updates.push({
          itemId: item.itemId,
          trackingEnabled: draftTracking,
          quantity: draftQty,
          lowStockThreshold: draftThreshold,
        });
      }
    });
  });
  return updates;
};
